import { useState, useEffect } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Settings, Users, CreditCard, Copy, Download, Eye, EyeOff, MoreHorizontal, Trash2, Crown, UserPlus, Edit, Send } from "lucide-react"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { toast } from "sonner"
import { useOrganization } from "@/contexts/organization-context"
import { useAuth } from "@/contexts/auth-context"
import { useRoleAccess } from "@/hooks/useRoleAccess"
import { usersService, User } from "@/services/users"
import { organizationService } from "@/services/organizations"
import { getBuildVersion, parseBuildVersion } from "@/utils/version"
import { billingService, UsageResponse, SubscriptionInfoResponse, PaymentMethod, Invoice } from "@/services/billing"

// Helper functions
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)
  
  if (diffHours < 24) {
    return diffHours === 0 ? 'Just now' : `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  } else {
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  }
}

const getUserRole = (user: User): string => {
  // Get the primary role for this organization
  const role = user.roles?.[0]?.name || 'viewer'
  return role
}

const getUserStatus = (user: User): 'active' | 'pending' | 'inactive' => {
  // Only use sign-in data to determine status
  if (user.last_sign_in_at) {
    const lastSignIn = new Date(user.last_sign_in_at)
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    return lastSignIn > thirtyDaysAgo ? 'active' : 'inactive'
  }

  // If no sign-in data, assume active (legacy users without event tracking)
  // Once user_events is populated, this will be replaced with actual activity data
  return 'active'
}


export default function SettingsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { currentOrganization, refreshOrganizations } = useOrganization()
  const { user: currentUser } = useAuth()
  const { hasSettingsAccess, hasAdminAccess, isOwner } = useRoleAccess()

  // Redirect if user doesn't have access
  useEffect(() => {
    if (currentOrganization && !hasSettingsAccess()) {
      console.warn('User does not have permission to access settings page')
      navigate(`/${orgSlug}`, { replace: true })
      return
    }
  }, [currentOrganization, orgSlug, navigate])

  // Show loading while organization data is being fetched
  if (!currentOrganization) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    )
  }

  // Don't render if user doesn't have access (will redirect via useEffect)
  if (!hasSettingsAccess()) {
    return null
  }
  
  const [activeTab, setActiveTab] = useState("general")
  const [apiKeyVisible, setApiKeyVisible] = useState(false)
  const [mcpKeyVisible, setMcpKeyVisible] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [userToEdit, setUserToEdit] = useState<User | null>(null)
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false)
  const [newRole, setNewRole] = useState<string>('')
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)

  // Invite user dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role: 'viewer'
  })
  const [inviteLoading, setInviteLoading] = useState(false)

  // Delete organization confirmation state
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('')

  // Net usage state for billing
  const [usageData, setUsageData] = useState<UsageResponse | null>(null)
  const [usageLoading, setUsageLoading] = useState(false)
  const [usagePeriod, setUsagePeriod] = useState<'current' | 'last'>('current')
  const [changePlanOpen, setChangePlanOpen] = useState(false)

  // Subscription info state
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfoResponse | null>(null)
  const [subscriptionLoading, setSubscriptionLoading] = useState(false)

  // Subscription emails state
  const [subscriptionEmails, setSubscriptionEmails] = useState('')
  const [subscriptionEmailsSaving, setSubscriptionEmailsSaving] = useState(false)


  // Handle tab navigation via URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (['general', 'billing', 'users'].includes(hash)) {
      setActiveTab(hash)
    }
  }, [location])

  // Load users when component mounts or org changes
  useEffect(() => {
    const loadUsers = async () => {
      if (!orgSlug) return
      
      setUsersLoading(true)
      try {
        const userData = await usersService.getOrganizationUsers(orgSlug)
        setUsers(userData)
      } catch (error) {
        console.error('Failed to load users:', error)
        toast.error('Failed to load users')
      } finally {
        setUsersLoading(false)
      }
    }

    loadUsers()
  }, [orgSlug])

  // Helper to get year-month string for last month
  const getLastMonthYearMonth = () => {
    const now = new Date()
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    return `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`
  }

  // Load usage data when billing tab is active or period changes
  useEffect(() => {
    const loadUsageData = async () => {
      if (activeTab !== 'billing') return

      setUsageLoading(true)
      try {
        const yearMonth = usagePeriod === 'last' ? getLastMonthYearMonth() : undefined
        const data = await billingService.getUsage(yearMonth)
        setUsageData(data)
      } catch (error) {
        console.error('Failed to load usage data:', error)
      } finally {
        setUsageLoading(false)
      }
    }

    loadUsageData()
  }, [activeTab, usagePeriod])

  // After returning from Stripe Checkout (?upgraded=1), the org only flips to paid
  // once the activation webhook lands — which can be a few seconds after redirect.
  // Poll the gate state until it reports paid, then reload once (dropping the
  // param) so the navbar "Free trial" badge and this card refresh with no manual
  // refresh. Gives up quietly after ~60s.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('upgraded') !== '1') return

    let stopped = false
    let attempts = 0
    const MAX_ATTEMPTS = 20
    const INTERVAL_MS = 3000

    const cleanPath = () => {
      params.delete('upgraded')
      const qs = params.toString()
      return window.location.pathname + (qs ? `?${qs}` : '')
    }

    const poll = async () => {
      if (stopped) return
      attempts += 1
      try {
        const u = await billingService.getUsage()
        // Paid = no longer on the free trial (and not an exempt/invoice org).
        if (u && u.is_exempt === false && u.is_free === false) {
          stopped = true
          window.location.replace(cleanPath()) // clean reload → badge + card refresh
          return
        }
      } catch {
        /* transient — keep polling */
      }
      if (!stopped && attempts < MAX_ATTEMPTS) {
        window.setTimeout(poll, INTERVAL_MS)
      } else if (!stopped) {
        // Give up quietly; strip the param so we don't re-poll on next mount.
        window.history.replaceState({}, '', cleanPath())
      }
    }

    const t = window.setTimeout(poll, 1500) // brief head start for the webhook
    return () => {
      stopped = true
      window.clearTimeout(t)
    }
  }, [])

  // Load subscription info when billing tab is active
  useEffect(() => {
    const loadSubscriptionData = async () => {
      if (activeTab !== 'billing') return

      setSubscriptionLoading(true)
      try {
        const data = await billingService.getSubscriptionInfo()
        setSubscriptionInfo(data)
        setSubscriptionEmails(data.emails || '')

        // Load payment methods and invoices only for credit_card plan type
        if (data.plan_type === 'credit_card') {
          // Load payment methods
          setPaymentMethodsLoading(true)
          try {
            const methods = await billingService.getPaymentMethods()
            setPaymentMethods(methods)
          } catch (error) {
            console.error('Failed to load payment methods:', error)
          } finally {
            setPaymentMethodsLoading(false)
          }

          // Load invoices
          setInvoicesLoading(true)
          try {
            const invoiceData = await billingService.getInvoices()
            setInvoices(invoiceData)
          } catch (error) {
            console.error('Failed to load invoices:', error)
          } finally {
            setInvoicesLoading(false)
          }
        }
      } catch (error) {
        console.error('Failed to load subscription info:', error)
      } finally {
        setSubscriptionLoading(false)
      }
    }

    loadSubscriptionData()
  }, [activeTab])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    navigate(`#${value}`, { replace: true })
  }

  const copyApiKey = () => {
    const apiKey = currentOrganization?.api_key || "your-api-key-here"
    navigator.clipboard.writeText(apiKey)
    toast.success("API key copied to clipboard")
  }

  const mcpKeyUrl = `https://mcp.glaium.io/${currentOrganization?.api_key || "your-api-key-here"}/mcp`

  const copyMcpKey = () => {
    navigator.clipboard.writeText(mcpKeyUrl)
    toast.success("MCP key copied to clipboard")
  }

  // Two billing-action handlers, one button surface:
  //
  // - handleManageSubscription: org already has a Stripe customer (the common
  //   case post-onboarding). Opens the Stripe-hosted Customer Portal where the
  //   user can update card, see invoices, change plan, or cancel.
  //
  // - handleSubscribeNow: legacy/edge case where the org has no
  //   stripe_customer_id (created before the onboarding-checkout flow, or via
  //   external provisioning that didn't capture payment). Starts a Stripe
  //   Checkout to attach a card and a subscription. Returns to /settings on
  //   success so the user lands back where they clicked.
  const [billingActionLoading, setBillingActionLoading] = useState(false)

  const handleManageSubscription = async () => {
    if (billingActionLoading) return
    setBillingActionLoading(true)
    try {
      const { url } = await billingService.createPortalSession(`/${orgSlug}/settings`)
      if (!url) throw new Error('No portal URL returned by the billing service')
      window.location.href = url
    } catch (error: any) {
      console.error('Failed to open billing portal:', error)
      const detail = error?.response?.data?.detail
      toast.error(detail || error?.message || 'Could not open the billing portal. Please try again.')
      setBillingActionLoading(false)
    }
  }

  const handleSubscribeNow = async () => {
    if (billingActionLoading) return
    setBillingActionLoading(true)
    try {
      // Settings-initiated checkout returns the user back to settings, not
      // through the onboarding org-creation funnel. The webhook handler
      // (api stripe_event_dispatcher.checkout_session_completed) stamps the
      // customer with the user_id; we don't need to read session_id back on
      // the return because no org-create is happening.
      const { url } = await billingService.createCheckoutSession({
        success_path: `/${orgSlug}/settings`,
        cancel_path: `/${orgSlug}/settings`,
      })
      if (!url) throw new Error('No checkout URL returned by the billing service')
      window.location.href = url
    } catch (error: any) {
      console.error('Failed to start Stripe Checkout:', error)
      const detail = error?.response?.data?.detail
      toast.error(detail || error?.message || 'Could not start payment setup. Please try again.')
      setBillingActionLoading(false)
    }
  }

  const handleSaveSubscriptionEmails = async () => {
    if (subscriptionEmailsSaving) return
    setSubscriptionEmailsSaving(true)
    try {
      await billingService.updateSubscriptionEmails(subscriptionEmails)
      // Update local state
      if (subscriptionInfo) {
        setSubscriptionInfo({ ...subscriptionInfo, emails: subscriptionEmails })
      }
      toast.success("Subscription emails updated")
    } catch (error) {
      console.error('Failed to update subscription emails:', error)
      toast.error("Failed to update subscription emails")
    } finally {
      setSubscriptionEmailsSaving(false)
    }
  }

  const handleSetDefaultPaymentMethod = async (paymentMethodId: string) => {
    try {
      await billingService.setDefaultPaymentMethod(paymentMethodId)
      // Update local state
      setPaymentMethods(prev => prev.map(pm => ({
        ...pm,
        default: pm.id === paymentMethodId
      })))
      toast.success("Default payment method updated")
    } catch (error) {
      console.error('Failed to set default payment method:', error)
      toast.error("Failed to update default payment method")
    }
  }

  const handleRemovePaymentMethod = async (paymentMethodId: string) => {
    try {
      await billingService.removePaymentMethod(paymentMethodId)
      // Update local state
      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId))
      toast.success("Payment method removed")
    } catch (error: any) {
      console.error('Failed to remove payment method:', error)
      const message = error?.response?.data?.detail || "Failed to remove payment method"
      toast.error(message)
    }
  }

  const isDeleteConfirmationValid = deleteConfirmationText === 'CONFIRMED'
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleDeleteOrganization = async () => {
    if (!isDeleteConfirmationValid || !currentOrganization) {
      toast.error('Please type "CONFIRMED" to delete the organization')
      return
    }

    setDeleteLoading(true)
    try {
      await organizationService.delete(currentOrganization.id)
      toast.success("Organization deleted successfully")
      setDeleteConfirmationText('')
      // Navigate to home - the organization context will handle refreshing the org list
      navigate('/')
    } catch (error: any) {
      console.error('Failed to delete organization:', error)
      const message = error?.response?.data?.detail || 'Failed to delete organization'
      toast.error(message)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleInviteUser = () => {
    setInviteDialogOpen(true)
  }

  const handleInviteSubmit = async () => {
    if (!orgSlug) return
    
    // Basic validation
    if (!inviteForm.email.trim()) {
      toast.error('Email is required')
      return
    }
    // Pydantic EmailStr on the api side rejects malformed addresses with a
    // generic validation error; catch it here so the user sees a clean inline
    // message instead of "Failed to invite user" after a roundtrip.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email.trim())) {
      toast.error('Please enter a valid email address')
      return
    }
    if (!inviteForm.name.trim()) {
      toast.error('Name is required')
      return
    }

    setInviteLoading(true)
    try {
      await usersService.inviteUser(orgSlug, {
        email: inviteForm.email.trim(),
        name: inviteForm.name.trim(),
        role: inviteForm.role
      })
      
      // Refresh users list
      const userData = await usersService.getOrganizationUsers(orgSlug)
      setUsers(userData)
      
      // Reset form and close dialog
      setInviteForm({ name: '', email: '', role: 'viewer' })
      setInviteDialogOpen(false)
      toast.success('User invited successfully')
    } catch (error: any) {
      console.error('Failed to invite user:', error)
      toast.error(error.message || 'Failed to invite user')
    } finally {
      setInviteLoading(false)
    }
  }

  const handleInviteCancel = () => {
    setInviteForm({ name: '', email: '', role: 'viewer' })
    setInviteDialogOpen(false)
  }

  const handleEditUser = (user: User) => {
    const currentRole = getUserRole(user)
    setUserToEdit(user)
    setNewRole(currentRole)
    setShowEditRoleDialog(true)
  }

  const handleRoleUpdate = async () => {
    if (!orgSlug || !userToEdit || !newRole) return

    try {
      await usersService.updateUser(orgSlug, userToEdit.id, { role: newRole })

      // Refresh from the server so ownership transfer (the backend demotes the
      // previous owner to manager when promoting to owner) is reflected — both
      // the user list and the current user's own role-based permissions.
      await refreshOrganizations()
      const userData = await usersService.getOrganizationUsers(orgSlug)
      setUsers(userData)

      toast.success(`Role updated successfully! ${userToEdit.name || userToEdit.email} is now a ${newRole}.`)

      // Close dialog and reset state
      setShowEditRoleDialog(false)
      setUserToEdit(null)
      setNewRole('')
    } catch (error: any) {
      console.error('Failed to update user role:', error)
      toast.error(error?.message || 'Failed to update user role.')
    }
  }

  const openDeleteConfirmation = (user: User) => {
    setUserToDelete(user)
    setShowDeleteConfirmation(true)
  }

  const handleDeleteUser = async () => {
    if (!orgSlug || !userToDelete) return
    
    try {
      await usersService.removeUser(orgSlug, userToDelete.id)
      // Refresh users list
      const userData = await usersService.getOrganizationUsers(orgSlug)
      setUsers(userData)
      toast.success(`${userToDelete.name || userToDelete.email} has been removed from the organization`)
    } catch (error) {
      console.error('Failed to delete user:', error)
      toast.error('Failed to remove user. This feature requires backend implementation.')
    } finally {
      setShowDeleteConfirmation(false)
      setUserToDelete(null)
    }
  }

  const handleResendInvite = async (user: User) => {
    if (!orgSlug) return

    try {
      await usersService.resendInvite(orgSlug, user.id)
      toast.success(`Invitation email resent to ${user.email}`)
    } catch (error: any) {
      console.error('Failed to resend invite:', error)
      toast.error(error.message || 'Failed to resend invitation')
    }
  }

  // Using Slate color scheme
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-slate-900 text-slate-50 dark:bg-slate-100 dark:text-slate-900'
      case 'admin':
        return 'bg-slate-700 text-slate-50 dark:bg-slate-300 dark:text-slate-900'
      case 'editor':
        return 'bg-slate-500 text-slate-50 dark:bg-slate-400 dark:text-slate-900'
      case 'manager':
        return 'bg-slate-600 text-slate-50 dark:bg-slate-350 dark:text-slate-900'
      case 'viewer':
        return 'bg-slate-300 text-slate-900 dark:bg-slate-600 dark:text-slate-100'
      default:
        return 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-slate-800 text-slate-50 dark:bg-slate-200 dark:text-slate-900'
      case 'pending':
        return 'bg-slate-400 text-slate-900 dark:bg-slate-500 dark:text-slate-100'
      case 'inactive':
        return 'bg-slate-300 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
      default:
        return 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
    }
  }

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your organization settings and preferences.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            General
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Billing & Usage
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-6">
          {/* API Key */}
          <Card>
            <CardHeader>
              <CardTitle>API Key</CardTitle>
              <CardDescription>
                Use this key to access the Glaium REST API. View the{" "}
                <a
                  href="https://api.glaium.io/api-docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-500 underline"
                >
                  API documentation
                </a>
                {" "}for more information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    type={apiKeyVisible ? "text" : "password"}
                    value={currentOrganization?.api_key || "glaium_sk_test_123456789abcdef"}
                    readOnly
                    className="font-mono text-sm"
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setApiKeyVisible(!apiKeyVisible)}
                >
                  {apiKeyVisible ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                <Button variant="outline" size="icon" onClick={copyApiKey}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* MCP Key */}
          <Card>
            <CardHeader>
              <CardTitle>MCP Key</CardTitle>
              <CardDescription>
                Connect Glaium to MCP-compatible clients (Claude, Cursor, etc.) using this URL.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <div className="flex-1">
                  <Input
                    type={mcpKeyVisible ? "text" : "password"}
                    value={mcpKeyUrl}
                    readOnly
                    className="font-mono text-sm"
                  />
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setMcpKeyVisible(!mcpKeyVisible)}
                >
                  {mcpKeyVisible ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                <Button variant="outline" size="icon" onClick={copyMcpKey}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* App Version */}
          <Card>
            <CardHeader>
              <CardTitle>App Version</CardTitle>
              <CardDescription>
                Current application version and build information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(() => {
                const version = getBuildVersion()
                const parsed = parseBuildVersion(version)

                return (
                  <div className="space-y-2 text-sm">
                    {parsed.formattedDate ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Build Date:</span>
                          <span className="font-mono">{parsed.formattedDate}</span>
                        </div>
                        {parsed.commitHash && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Commit:</span>
                            <span className="font-mono text-xs">{parsed.commitHash}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted-foreground">
                        Development version
                      </div>
                    )}
                  </div>
                )
              })()}
            </CardContent>
          </Card>

          {/* Danger Zone - Only visible to owner and admin */}
          {(isOwner() || hasAdminAccess()) && (
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader>
                <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
                <CardDescription>
                  Irreversible and destructive actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive">
                      Delete Organization
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{(currentUser?.first_name || currentUser?.name?.split(' ')[0] || '').charAt(0).toUpperCase() + (currentUser?.first_name || currentUser?.name?.split(' ')[0] || '').slice(1).toLowerCase()}, are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete your
                        organization and remove all associated data from our servers.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                      <Label htmlFor="delete-confirmation">
                        Type <span className="font-semibold">CONFIRMED</span> to confirm deletion:
                      </Label>
                      <Input
                        id="delete-confirmation"
                        type="text"
                        value={deleteConfirmationText}
                        onChange={(e) => setDeleteConfirmationText(e.target.value)}
                        placeholder="Type CONFIRMED here"
                        className="mt-2"
                        autoComplete="off"
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel onClick={() => setDeleteConfirmationText('')} disabled={deleteLoading}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteOrganization}
                        disabled={!isDeleteConfirmationValid || deleteLoading}
                        className="bg-red-600 focus:ring-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        {deleteLoading ? 'Deleting...' : 'Delete Organization'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Billing & Usage Tab */}
        <TabsContent value="billing" className="space-y-6">
          {/* Current Plan */}
          <Card>
            <CardHeader>
              <CardTitle>
                Current Plan{subscriptionInfo?.plan_name ? `: ${subscriptionInfo.plan_name}` : ''}
              </CardTitle>
              <CardDescription>
                Manage your subscription and billing information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {subscriptionLoading ? (
                <div className="flex items-center justify-center py-4">
                  <span className="text-muted-foreground">Loading subscription info...</span>
                </div>
              ) : subscriptionInfo?.has_subscription ? (
                <>
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge className={subscriptionInfo.is_active
                        ? "bg-slate-800 text-slate-50 dark:bg-slate-200 dark:text-slate-900"
                        : "bg-slate-400 text-slate-900"
                      }>
                        {subscriptionInfo.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      <div className="text-sm text-muted-foreground">
                        {subscriptionInfo.current_tier && (
                          <span>Tier: {subscriptionInfo.current_tier.tier_name}</span>
                        )}
                        {(subscriptionInfo.plan_type || subscriptionInfo.frequency) && (
                          <span className={subscriptionInfo.current_tier ? "ml-2" : ""}>
                            {subscriptionInfo.current_tier && "• "}
                            <span className="capitalize">{subscriptionInfo.plan_type?.replace('_', ' ') || ''}</span>
                            {subscriptionInfo.plan_type && subscriptionInfo.frequency && '/'}
                            {subscriptionInfo.frequency}
                          </span>
                        )}
                        {subscriptionInfo.expiration_date && (
                          <span className="ml-2">
                            • Expiration: {new Date(subscriptionInfo.expiration_date).toLocaleDateString()}
                          </span>
                        )}
                        {subscriptionInfo.is_trialing && subscriptionInfo.expiration_date && (() => {
                          // Days remaining = ceil( (expiration - now) / day ).
                          // ceil instead of floor so a trial ending tomorrow at any
                          // time today shows "1 day", not "0 days".
                          const msPerDay = 1000 * 60 * 60 * 24
                          const daysRemaining = Math.max(
                            0,
                            Math.ceil(
                              (new Date(subscriptionInfo.expiration_date).getTime() - Date.now()) / msPerDay
                            )
                          )
                          return (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                              Free trial — {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                            </Badge>
                          )
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleManageSubscription}
                        disabled={billingActionLoading}
                      >
                        {billingActionLoading ? 'Opening…' : 'Manage Subscription'}
                      </Button>
                      {hasAdminAccess() && subscriptionInfo.tiers.length > 0 && (
                        <Button variant="outline" size="sm" onClick={() => setChangePlanOpen(true)}>
                          Change Plan
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subscription-emails">Notification Emails</Label>
                    <Input
                      id="subscription-emails"
                      type="text"
                      placeholder="Comma-separated list of emails. Press Enter or click outside to save."
                      value={subscriptionEmails}
                      onChange={(e) => setSubscriptionEmails(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveSubscriptionEmails()
                        }
                      }}
                      onBlur={handleSaveSubscriptionEmails}
                      disabled={subscriptionEmailsSaving}
                      className="text-sm"
                    />
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold">No Active Subscription</h3>
                    <p className="text-sm text-muted-foreground">
                      Subscribe to access premium features
                    </p>
                  </div>
                  <Button
                    onClick={handleSubscribeNow}
                    disabled={billingActionLoading}
                  >
                    {billingActionLoading ? 'Redirecting…' : 'Subscribe Now'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Usage */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Usage</CardTitle>
                  <CardDescription>
                    Monthly usage and limits
                  </CardDescription>
                </div>
                <Select
                  value={usagePeriod}
                  onValueChange={(value: 'current' | 'last') => setUsagePeriod(value)}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current Month</SelectItem>
                    <SelectItem value="last">Last Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Data Processing</span>
                  <span>
                    {usageLoading ? (
                      'Loading...'
                    ) : usageData ? (
                      <>
                        {(Math.max(0, usageData.mtd_records) + Math.max(0, usageData.mtd_backfill_records ?? 0)).toLocaleString()} records
                        {subscriptionInfo?.current_tier?.cap_volume ? (
                          <span className="text-muted-foreground">
                            {' '}/ {subscriptionInfo.current_tier.cap_volume.toLocaleString()}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      '0 records'
                    )}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 flex overflow-hidden">
                  {(() => {
                    const cap = subscriptionInfo?.current_tier?.cap_volume
                    const current = usageData ? Math.max(0, usageData.mtd_records) : 0
                    const backfill = usageData ? Math.max(0, usageData.mtd_backfill_records ?? 0) : 0
                    const totalPercent = cap ? ((current + backfill) / cap) * 100 : 0

                    const [currentColor, backfillColor] = totalPercent > 100
                      ? ['bg-red-600', 'bg-red-400']
                      : totalPercent >= 85
                        ? ['bg-yellow-500', 'bg-yellow-300']
                        : ['bg-green-600', 'bg-green-400']

                    const rawCurrent = cap ? (current / cap) * 100 : 0
                    const rawBackfill = cap ? (backfill / cap) * 100 : 0
                    const totalRaw = rawCurrent + rawBackfill
                    const scale = totalRaw > 100 ? 100 / totalRaw : 1
                    const currentWidth = rawCurrent * scale
                    const backfillWidth = rawBackfill * scale

                    return (
                      <>
                        <div
                          className={`${currentColor} h-2 transition-all duration-300`}
                          style={{ width: `${currentWidth}%` }}
                        />
                        <div
                          className={`${backfillColor} h-2 transition-all duration-300`}
                          style={{ width: `${backfillWidth}%` }}
                        />
                      </>
                    )
                  })()}
                </div>
                {usageData && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Month: {usageData.billing_period.start.substring(0, 7)}</span>
                    <span>
                      Current: {Math.max(0, usageData.mtd_records).toLocaleString()}
                      {' • '}
                      Backfill: {Math.max(0, usageData.mtd_backfill_records ?? 0).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Change Plan Dialog - only show for admins when subscription exists */}
          {subscriptionInfo?.has_subscription && subscriptionInfo.tiers.length > 0 && hasAdminAccess() && (
              <Dialog open={changePlanOpen} onOpenChange={setChangePlanOpen}>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Change Subscription Plan</DialogTitle>
                    <DialogDescription>
                      Select a new tier for your organization. Changes will be applied immediately.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Current Tier</p>
                      <p className="text-sm text-muted-foreground">
                        {subscriptionInfo.current_tier ? (
                          <>
                            {subscriptionInfo.current_tier.tier_name} - ${subscriptionInfo.current_tier.price.toLocaleString()}/month
                          </>
                        ) : (
                          'No tier set'
                        )}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Select New Tier</p>
                      <div className="grid grid-cols-2 gap-3">
                        {subscriptionInfo.tiers.filter((tier) => tier.tier_name !== 'stripe_price_id').map((tier) => (
                          <button
                            key={tier.tier_name}
                            onClick={async () => {
                              try {
                                await billingService.updateSubscriptionTier(tier.tier_name)
                                toast.success('Plan updated successfully')
                                const data = await billingService.getSubscriptionInfo()
                                setSubscriptionInfo(data)
                                setChangePlanOpen(false)
                              } catch (error: any) {
                                toast.error(`Failed to update plan: ${error.response?.data?.detail || error.message}`)
                              }
                            }}
                            className={`p-3 rounded-lg border text-left transition-colors hover:border-primary ${
                              tier.tier_name === subscriptionInfo.subscription_tier
                                ? 'border-primary bg-primary/5'
                                : 'border-muted hover:bg-muted/50'
                            }`}
                          >
                            <p className="font-medium text-sm">{tier.tier_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {tier.base_volume.toLocaleString()} - {tier.cap_volume.toLocaleString()} records
                            </p>
                            <p className="text-sm font-semibold mt-1">${tier.price.toLocaleString()}/mo</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setChangePlanOpen(false)}>
                      Cancel
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
          )}

          {/* Payment Methods - only show for credit_card plan types */}
          {subscriptionInfo?.plan_type === 'credit_card' && (
            <Card>
              <CardHeader>
                <CardTitle>Payment Methods</CardTitle>
                <CardDescription>
                  Manage your payment methods and billing information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {paymentMethodsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <span className="text-muted-foreground">Loading payment methods...</span>
                  </div>
                ) : paymentMethods.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    No payment methods found
                  </div>
                ) : (
                  paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="px-2 h-6 bg-slate-200 rounded flex items-center justify-center text-[10px] font-medium uppercase whitespace-nowrap">
                          {method.brand}
                        </div>
                        <div>
                          <p className="font-medium">
                            {formatCardBrand(method.brand)} ending in {method.last4}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Expires {method.exp_month}/{method.exp_year}
                          </p>
                        </div>
                        {method.default && (
                          <Badge className="bg-slate-700 text-slate-50 dark:bg-slate-300 dark:text-slate-900">Default</Badge>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!method.default && (
                            <DropdownMenuItem onClick={() => handleSetDefaultPaymentMethod(method.id)}>
                              Set as Default
                            </DropdownMenuItem>
                          )}
                          {!method.default && (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleRemovePaymentMethod(method.id)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Remove
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))
                )}
                <Button variant="outline">Add Payment Method</Button>
              </CardContent>
            </Card>
          )}

          {/* Billing History - only show for credit_card plan types */}
          {subscriptionInfo?.plan_type === 'credit_card' && (
            <Card>
              <CardHeader>
                <CardTitle>Billing History</CardTitle>
                <CardDescription>
                  Download invoices and view payment history
                </CardDescription>
              </CardHeader>
              <CardContent>
                {invoicesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <span className="text-muted-foreground">Loading invoices...</span>
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    No invoices found
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Invoice</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            {new Date(invoice.created * 1000).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="font-mono">{invoice.number || '-'}</TableCell>
                          <TableCell>{formatCurrency(invoice.amount_paid / 100)}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                invoice.paid
                                  ? 'bg-slate-800 text-slate-50 dark:bg-slate-200 dark:text-slate-900'
                                  : invoice.status === 'open'
                                  ? 'bg-slate-400 text-slate-900 dark:bg-slate-500 dark:text-slate-100'
                                  : 'bg-slate-600 text-slate-100 dark:bg-slate-400 dark:text-slate-900'
                              }
                            >
                              {invoice.paid ? 'paid' : invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {invoice.invoice_pdf && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(invoice.invoice_pdf!, '_blank')}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Team Members</CardTitle>
                <CardDescription>
                  Manage your organization's team members and their permissions
                </CardDescription>
              </div>
              <Button onClick={handleInviteUser} className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Invite User
              </Button>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-pulse text-muted-foreground">Loading users...</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Active</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users
                      .sort((a, b) => {
                        // Sort alphabetically by name (or email if no name)
                        const nameA = (a.name || a.email).toLowerCase()
                        const nameB = (b.name || b.email).toLowerCase()
                        return nameA.localeCompare(nameB)
                      })
                      .map((user) => {
                      const role = getUserRole(user)
                      const status = getUserStatus(user)
                      // Use last_sign_in_at (most recent activity from user_events)
                      const lastActiveDate = user.last_sign_in_at
                      const lastActive = lastActiveDate
                        ? getRelativeTime(lastActiveDate)
                        : 'Never'
                      
                      
                      return (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium">
                                {user.name ? user.name.split(' ').map(n => n[0]).join('') : user.email.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  {user.name || user.email}
                                  {role === 'owner' && (
                                    <Crown className="h-4 w-4 text-slate-600" />
                                  )}
                                </p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleColor(role)}>
                              {role}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(status)}>
                              {status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {lastActive}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditUser(user)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Role
                                </DropdownMenuItem>
                                {!user.invitation_accepted_at && (
                                  <DropdownMenuItem onClick={() => handleResendInvite(user)}>
                                    <Send className="mr-2 h-4 w-4" />
                                    Resend Invite
                                  </DropdownMenuItem>
                                )}
                                {role !== 'owner' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => openDeleteConfirmation(user)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Remove User
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Send an invitation to add a new user to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Full Name</Label>
              <Input
                id="invite-name"
                placeholder="Enter full name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                disabled={inviteLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="Enter email address"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                disabled={inviteLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm(prev => ({ ...prev, role: value }))}
                disabled={inviteLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={handleInviteCancel}
              disabled={inviteLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleInviteSubmit}
              disabled={inviteLoading}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{userToDelete?.name || userToDelete?.email}</strong> from this organization?
              <br /><br />
              This action cannot be undone. The user will lose access to all organization data and resources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDeleteConfirmation(false)
              setUserToDelete(null)
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditRoleDialog} onOpenChange={setShowEditRoleDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
            <DialogDescription>
              Change the role for <strong>{userToEdit?.name || userToEdit?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label htmlFor="role" className="text-sm font-medium">
                Role
              </label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  {(hasAdminAccess() || isOwner()) && (
                    <SelectItem value="owner">Owner</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {newRole === 'viewer' && 'Can view organization data but cannot make changes'}
                {newRole === 'editor' && 'Can view and edit organization data'}
                {newRole === 'manager' && 'Can manage organization settings and users'}
                {newRole === 'owner' && 'Has full control over the organization and can transfer ownership'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditRoleDialog(false)
                setUserToEdit(null)
                setNewRole('')
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRoleUpdate}
              disabled={!newRole || newRole === getUserRole(userToEdit!)}
            >
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  )
}